import { Save, Start } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
  Theme
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { FC, useEffect, useState } from 'react';
import {
  createDocumentTemplate,
  getContentLibraryDocuments,
  getTemplates
} from '../../../../fetch';
import {
  IContentLibraryDocument,
  IDocumentTemplate,
  IVendorContact,
  IVendorDetail,
  IPostContentLibrary,
  CONTENT_PLACE_HOLDER_BLOCKS
} from '../../../../models';

interface IVendorInitContract {
  currentVendor: IVendorDetail;
  fetchCurrentVendor: (id: number) => Promise<void>;
  isSubcontractor: boolean;
  vendorContacts: IVendorContact[];
  isLoadingVendorContacts: boolean;
}

export const VendorInitContract: FC<IVendorInitContract> = ({
  fetchCurrentVendor,
  isSubcontractor,
  currentVendor,
  vendorContacts,
  isLoadingVendorContacts
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedTemplate, setSelectedTemplate] = useState<IDocumentTemplate | null>(null);
  const [selectedExhibitATemplate, setSelectedExhibitATemplate] =
    useState<IContentLibraryDocument | null>(null);
  const [selectedExhibitCTemplate, setSelectedExhibitCTemplate] =
    useState<IContentLibraryDocument | null>(null);
  const [hasContractFlow, setContractFlow] = useState(false);
  const [selectedVendorContact, setSelectedVendorContact] = useState<IVendorContact | string>('');
  const [templates, setTemplates] = useState<IDocumentTemplate[]>([]);
  const [isLoadingTemplates, setLoadingTemplates] = useState<boolean>(true);
  const [exhibitATemplates, setExhibitATemplates] = useState<IContentLibraryDocument[]>([]);
  const [exhibitCTemplates, setExhibitCTemplates] = useState<IContentLibraryDocument[]>([]);
  const [isLoadingExhibitATemplates, setLoadingExhibitATemplates] = useState<boolean>(true);
  const [isLoadingExhibitCTemplates, setLoadingExhibitCTemplates] = useState<boolean>(true);
  const [isSubmitting, setSubmitting] = useState<boolean>(false);
  const [showInitiateButton, setShowInitiateButton] = useState(true);
  const [noVendorContactErrorMessage, setNoVendorContactErrorMessage] = useState<string>('');
  const [selectedContentLibraries, setSelectedContentLibraries] = useState<IPostContentLibrary[]>(
    []
  );

  const classes = useStyles();

  const fetchTemplates = async () => {
    try {
      const res = await getTemplates({
        vendorTypeId: currentVendor.vendorTypeId,
        vendorId: currentVendor.vendorId,
        subcontractorTypeId: currentVendor.subcontractorTypeId
      });
      setTemplates(res.records);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchExhibitATemplates = async () => {
    try {
      const res = await getContentLibraryDocuments({
        contentPlaceHolderBlock: 'ScopeOfWork',
        vendorTypeId: currentVendor.vendorTypeId,
        subcontractorTypeId: currentVendor.subcontractorTypeId
      });
      setExhibitATemplates(res.records);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingExhibitATemplates(false);
    }
  };

  const fetchExhibitCTemplates = async () => {
    try {
      const res = await getContentLibraryDocuments({
        contentPlaceHolderBlock: 'PriceSheet',
        vendorTypeId: currentVendor.vendorTypeId,
        subcontractorTypeId: currentVendor.subcontractorTypeId
      });
      setExhibitCTemplates(res.records);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingExhibitCTemplates(false);
    }
  };

  const contractManagers = vendorContacts?.filter(vc => vc.roles?.includes('ContractsManager'));
  useEffect(() => {
    if (contractManagers?.length === 0) {
      setSelectedVendorContact('');
      setNoVendorContactErrorMessage('Contract manager needs to be added.');
    } else {
      setNoVendorContactErrorMessage('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorContacts, contractManagers]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      fetchTemplates();
    }
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let mounted = true;
    if (mounted && selectedTemplate?.contentPlaceHolderBlocks?.includes('ScopeOfWork')) {
      fetchExhibitATemplates();
    }
    if (mounted && selectedTemplate?.contentPlaceHolderBlocks?.includes('PriceSheet')) {
      fetchExhibitCTemplates();
    }
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  const handleReset = () => {
    setContractFlow(false);
    setSelectedVendorContact('');
    setSelectedTemplate(null);
    setSelectedExhibitATemplate(null);
    setSelectedExhibitCTemplate(null);
  };

  return (
    <Box marginBottom="1rem">
      <Grid container spacing={1} className={classes.marginBottom}>
        {!hasContractFlow && showInitiateButton && (
          <Grid item>
            <Button
              startIcon={<Start />}
              onClick={() => {
                setContractFlow(true);
              }}
              variant="contained"
              color="primary"
            >
              {`Initiate New ${isSubcontractor ? 'Subcontractor' : 'Vendor'} Agreement Workflow`}
            </Button>
          </Grid>
        )}
        {hasContractFlow && (
          <>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" required>
                <Autocomplete
                  value={selectedTemplate}
                  classes={{
                    paper: classes.paperBorder
                  }}
                  onChange={(event, newValue: IDocumentTemplate) => {
                    if (newValue && newValue?.name?.length > 0) {
                      setSelectedTemplate(newValue);
                    } else {
                      setSelectedTemplate(null);
                    }
                  }}
                  id="templates"
                  options={templates}
                  disabled={isLoadingTemplates}
                  getOptionLabel={(option: IDocumentTemplate) => {
                    return option.name;
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      size="small"
                      name="templates"
                      required
                      autoComplete="none"
                      label="Template"
                      variant="outlined"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            {selectedTemplate?.contentPlaceHolderBlocks?.includes('ScopeOfWork') && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined" required>
                  <Autocomplete
                    value={selectedExhibitATemplate}
                    classes={{
                      paper: classes.paperBorder
                    }}
                    onChange={(event, newValue: IContentLibraryDocument) => {
                      if (newValue && newValue?.documentName?.length > 0) {
                        setSelectedExhibitATemplate(newValue);
                        setSelectedContentLibraries([
                          ...selectedContentLibraries,
                          {
                            contentPlaceHolderBlock: 'ScopeOfWork' as CONTENT_PLACE_HOLDER_BLOCKS,
                            contentLibraryId: newValue.pandaDocContentLibraryDocumentId
                          }
                        ]);
                      } else {
                        setSelectedExhibitATemplate(null);
                        setSelectedContentLibraries(
                          selectedContentLibraries.filter(
                            l => l.contentPlaceHolderBlock !== 'ScopeOfWork'
                          )
                        );
                      }
                    }}
                    id="exhibitATemplates"
                    options={exhibitATemplates}
                    disabled={isLoadingExhibitATemplates}
                    getOptionLabel={(option: IContentLibraryDocument) => {
                      return option.documentName;
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        size="small"
                        name="exhibitATemplates"
                        autoComplete="none"
                        label="Exhibit A Template"
                        variant="outlined"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            )}
            {selectedTemplate?.contentPlaceHolderBlocks?.includes('PriceSheet') && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined" required>
                  <Autocomplete
                    value={selectedExhibitCTemplate}
                    classes={{
                      paper: classes.paperBorder
                    }}
                    onChange={(event, newValue: IContentLibraryDocument) => {
                      if (newValue && newValue?.documentName?.length > 0) {
                        setSelectedExhibitCTemplate(newValue);
                        setSelectedContentLibraries([
                          ...selectedContentLibraries,
                          {
                            contentPlaceHolderBlock: 'PriceSheet' as CONTENT_PLACE_HOLDER_BLOCKS,
                            contentLibraryId: newValue.pandaDocContentLibraryDocumentId
                          }
                        ]);
                      } else {
                        setSelectedExhibitCTemplate(null);
                        setSelectedContentLibraries(
                          selectedContentLibraries.filter(
                            l => l.contentPlaceHolderBlock !== 'PriceSheet'
                          )
                        );
                      }
                    }}
                    id="exhibitCTemplates"
                    options={exhibitCTemplates}
                    disabled={isLoadingExhibitCTemplates}
                    getOptionLabel={(option: IContentLibraryDocument) => {
                      return option.documentName;
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        size="small"
                        required
                        name="exhibitCTemplates"
                        autoComplete="none"
                        label="Exhibit C Template"
                        variant="outlined"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" required>
                <Autocomplete
                  value={selectedVendorContact}
                  classes={{
                    paper: classes.paperBorder
                  }}
                  onChange={(event, newValue: IVendorContact) => {
                    if (newValue && newValue?.name?.length) {
                      setSelectedVendorContact(newValue);
                    } else {
                      setSelectedVendorContact('');
                    }
                  }}
                  id="vendorContact"
                  options={contractManagers}
                  renderOption={(props, option: IVendorContact) => {
                    return (
                      <li {...props} key={option.personId}>
                        {option?.name} {option?.email ? `(${option?.email})` : `()`}
                      </li>
                    );
                  }}
                  disabled={isLoadingVendorContacts}
                  getOptionLabel={(option: IVendorContact) => {
                    return option ? `${option?.name}` : '';
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      error={!!noVendorContactErrorMessage}
                      size="small"
                      name="person"
                      required
                      autoComplete="none"
                      label="Person"
                      variant="outlined"
                    />
                  )}
                />
                {noVendorContactErrorMessage && (
                  <FormHelperText error>{noVendorContactErrorMessage}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <ButtonGroup>
                <LoadingButton
                  loading={isSubmitting}
                  loadingPosition="start"
                  startIcon={<Save />}
                  onClick={async () => {
                    setSubmitting(true);
                    try {
                      const res = await createDocumentTemplate({
                        personId: (selectedVendorContact as IVendorContact).personId,
                        vendorId: currentVendor.vendorId,
                        templateIds: selectedTemplate ? [selectedTemplate.pandaDocTemplateId] : [],
                        contentLibraries: selectedContentLibraries
                      });
                      if (res?.[0]?.error) {
                        enqueueSnackbar(`${res?.[0].error}`, {
                          variant: 'error'
                        });
                      } else {
                        enqueueSnackbar(`Contract Flow Initiated!`, {
                          variant: 'success'
                        });
                        handleReset();
                        fetchCurrentVendor(currentVendor.vendorId);
                        setShowInitiateButton(false);
                      }
                    } catch (error) {
                      enqueueSnackbar(error?.Detail ?? `Error submitting contract flow.`, {
                        variant: 'error'
                      });
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                  variant="contained"
                  color="primary"
                  disabled={
                    !selectedTemplate ||
                    !selectedVendorContact ||
                    isSubmitting
                  }
                >
                  Submit
                </LoadingButton>
                <Button
                  onClick={() => {
                    handleReset();
                  }}
                  variant="contained"
                  color="inherit"
                >
                  Cancel
                </Button>
              </ButtonGroup>
            </Grid>
          </>
        )}
      </Grid>
      {showInitiateButton && <Divider />}
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  paperBorder: {
    border: `1px solid ${theme.palette.grey[300]}`
  },
  marginBottom: {
    marginBottom: theme.spacing(1)
  }
}));
